import { Application } from "@/contexts/ApplicationContext";
import { useToast } from "@/hooks/use-toast";
import { Job } from "@/hooks/useJobQueries";
import { jobHasSegment } from "@/utils/stageProgressionUtils";
interface ValidationResult {
  isValid: boolean;
}

export const useBasicDetailsValidation = (job: Job) => {
  const { toast } = useToast();
  const validateForm = (application: Application): ValidationResult => {
    // Check if CV upload is required based on job configuration
    const basicDetailsStage = job.stages.find(
      (stage) => stage.id === "basic-details"
    );

    // Check CV upload requirements
    const cvSegment = basicDetailsStage?.segments.find(
      (segment) => segment.id === "cv-upload"
    );
    const cvRequired = cvSegment?.required === "true";

    if (
      !application.basicDetails.firstName ||
      !application.basicDetails.lastName
    ) {
      toast({
        title: "Error",
        description: "Please provide your full name",
        variant: "destructive",
      });
      return { isValid: false };
    }

    if (!application.basicDetails.email) {
      toast({
        title: "Error",
        description: "Please provide your email address",
        variant: "destructive",
      });
      return { isValid: false };
    }

    if (!application.basicDetails.phoneNumber) {
      toast({
        title: "Error",
        description: "Please provide your phone number",
        variant: "destructive",
      });
      return { isValid: false };
    }

    if (!application.basicDetails.dateOfBirth) {
      toast({
        title: "Error",
        description: "Please provide your date of birth",
        variant: "destructive",
      });
      return { isValid: false };
    }

    const dobSegment = basicDetailsStage?.segments.find(
      (segment) => segment.id === "dob"
    );
    // Safely read ageLimit from a meta bag that might be loosely typed
    const rawAgeLimit =
      (dobSegment as unknown as { meta?: Record<string, unknown> })?.meta?.[
        "ageLimit"
      ] ?? (dobSegment as unknown as { ageLimit?: unknown })?.ageLimit;
    const ageLimit =
      typeof rawAgeLimit === "number"
        ? rawAgeLimit
        : typeof rawAgeLimit === "string"
        ? Number(rawAgeLimit)
        : undefined;
    const today = new Date();
    const age = Math.floor(
      today.getFullYear() -
        new Date(application.basicDetails.dateOfBirth).getFullYear() -
        (today.getMonth() >=
        new Date(application.basicDetails.dateOfBirth).getMonth()
          ? 0
          : 1)
    );
    if (typeof ageLimit === "number" && age < ageLimit) {
      toast({
        title: "Ineligible",
        description: `You must be at least ${ageLimit} years old to apply`,
        variant: "destructive",
      });
      return { isValid: false };
    }

    if (
      jobHasSegment(job, "rtw-eligibility") &&
      (application.basicDetails.rightToWork === false ||
        application.basicDetails.rightToWork === undefined)
    ) {
      toast({
        title: "Ineligible",
        description: "You need the right to work in the UK to apply",
        variant: "destructive",
      });
      return { isValid: false };
    }

    //TODO: This will depend on the job requirements
    if (
      jobHasSegment(job, "driving-licence-eligibility") &&
      (application.basicDetails.validDrivingLicense === false ||
        application.basicDetails.validDrivingLicense === undefined)
    ) {
      toast({
        title: "Ineligible",
        description:
          "You need a valid driving license for over 1 year with less than 6 points",
        variant: "destructive",
      });
      return { isValid: false };
    }

    //TODO: This will depend on the job requirements
    if (
      jobHasSegment(job, "vehicle-information") &&
      application.basicDetails.ownElectricVan === undefined
    ) {
      toast({
        title: "Ineligible",
        description: "You need to specify if you own an electric van",
        variant: "destructive",
      });
      return { isValid: false };
    }

    if (
      jobHasSegment(job, "vehicle-information") &&
      application.basicDetails.ownElectricVan === false &&
      application.basicDetails.needsVan === undefined
    ) {
      toast({
        title: "Ineligible",
        description: "You need to specify if you need a van",
        variant: "destructive",
      });
      return { isValid: false };
    }

    if (
      jobHasSegment(job, "availability") &&
      application.basicDetails.workDays.length === 0
    ) {
      toast({
        title: "Error",
        description: "Please select the days you're available to work",
        variant: "destructive",
      });
      return { isValid: false };
    }

    // Check if CV is required and provided
    if (
      jobHasSegment(job, "cv-upload") &&
      cvRequired &&
      !application.basicDetails.cv
    ) {
      toast({
        title: "Error",
        description: "Please upload your CV",
        variant: "destructive",
      });
      return { isValid: false };
    }

    // Check if Reference Driver ID is required and provided
    if (
      jobHasSegment(job, "reference-driver-id") &&
      !application.referenceDriverId
    ) {
      toast({
        title: "Error",
        description: "Please provide a Reference Driver ID",
        variant: "destructive",
      });
      return { isValid: false };
    }

    return { isValid: true };
  };

  return { validateForm };
};
